import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { z } from 'zod';
import { settingResponseSchema } from '@shared/api';
import { toast } from '@web/hooks/useToast';
import { api, HTTPError } from '@web/lib/api';
import { keys } from '@web/lib/keys';

/**
 * One settings key, read and written.
 *
 * A key that has never been changed has no row, and the server answers 404 for
 * it. That is not an error state — it is "still on the default" — so it
 * resolves to the fallback. The alternative, seeding every key that might one
 * day be edited, means a migration every time a setting is added.
 */
export function useSetting<S extends z.ZodTypeAny>(
  key: string,
  schema: S,
  fallback: z.infer<S>,
): UseQueryResult<{ value: z.infer<S>; stored: boolean; updatedAt: string | null }> {
  return useQuery({
    queryKey: keys.setting(key),
    queryFn: async () => {
      try {
        const response = await api.get(`/api/settings/${key}`, settingResponseSchema);
        const parsed = schema.safeParse(response.setting.value);
        // A stored value that no longer fits its shape (an older release wrote
        // it, someone edited the row by hand) must not blank the editor: show
        // the default and let them save over it.
        return {
          value: (parsed.success ? parsed.data : fallback) as z.infer<S>,
          stored: parsed.success,
          updatedAt: response.setting.updatedAt,
        };
      } catch (error) {
        if (error instanceof HTTPError && error.status === 404) {
          return { value: fallback, stored: false, updatedAt: null };
        }
        throw error;
      }
    },
  });
}

export function useSaveSetting(key: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (value: unknown) => {
      const response = await api.put(`/api/settings/${key}`, settingResponseSchema, { value });
      return response.setting;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.setting(key) });
      toast('info', 'Saved');
    },
    onError: (error) => {
      if (error instanceof HTTPError && error.status === 403) {
        toast('warn', 'Admins only', 'Ask an administrator to make this change.');
        return;
      }
      toast('error', 'Not saved', 'The change was rejected. Nothing was written.');
    },
  });
}
